import { cn } from "@/lib/cn";
import { hasFloatingSurfaceAbove } from "./floating-above";
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStatus,
  useTransitionStyles,
} from "@floating-ui/react";
import { useAppDispatch, useAppSelector } from "@store";
import { setDialogId } from "@store/global-slice";
import { isDialogActive } from "@store/global-slice/selectors";
import * as React from "react";

interface DialogOptions {
  initialOpen?: boolean;
  open?: boolean;
  id?: string;
  onOpenChange?: (open: boolean) => void;
  closeOnOutsideClick?: boolean | (() => void);
  /**
   * Close on Escape. Off by default, which is what every dialog in the app has
   * done until now — floating-ui's dismiss behaviour is disabled wholesale here,
   * so nothing was listening for it. Opt in per dialog rather than turning it on
   * everywhere at once: a dialog holding a half-filled form would start throwing
   * the input away on a keystroke, which is a product call and not this one.
   */
  closeOnEscape?: boolean;
}

function useDialog({
  initialOpen = false,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  closeOnOutsideClick,
  closeOnEscape = false,
  id,
}: DialogOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);
  const [labelId, setLabelId] = React.useState<string | undefined>();
  const [descriptionId, setDescriptionId] = React.useState<
    string | undefined
  >();

  const isActive = useAppSelector(isDialogActive(id ?? ""));
  const dispatch = useAppDispatch();

  const open = id ? isActive : (controlledOpen ?? uncontrolledOpen);
  const setOpen = React.useMemo(
    () =>
      id
        ? () => dispatch(setDialogId({ id: null }))
        : (setControlledOpen ?? setUncontrolledOpen),
    [id, dispatch, setControlledOpen, setUncontrolledOpen],
  );

  const data = useFloating({
    open,
    onOpenChange: setOpen,
    strategy: "fixed",
  });

  const context = data.context;

  const click = useClick(context);
  // Nothing dismisses from here: the overlay decides what an outside press does
  // (gated on closeOnOutsideClick), and Escape is handled below, where it can
  // tell whether something floats above the dialog.
  const dismiss = useDismiss(context, {
    outsidePress: false,
    escapeKey: false,
    enabled: false,
  });
  const role = useRole(context);

  /**
   * Escape closes the dialog — unless a popover, menu or another dialog is open
   * above it, in which case Escape is theirs. floating-ui's own escapeKey could
   * not tell: a filter menu inside the share dialog opens in a floating tree of
   * its own, so both it and the dialog took the same keypress, and closing the
   * menu closed the dialog under it — selection, form state and all.
   */
  const floatingRef = data.refs.floating;
  React.useEffect(() => {
    if (!closeOnEscape || !open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (hasFloatingSurfaceAbove(floatingRef.current)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [closeOnEscape, open, setOpen, floatingRef]);

  const interactions = useInteractions([click, dismiss, role]);
  const { status } = useTransitionStatus(context);
  const { isMounted, styles } = useTransitionStyles(context, {
    duration: 200,
  });

  return React.useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
      closeOnOutsideClick,
      isMounted,
      styles,
      status,
    }),
    [
      open,
      setOpen,
      interactions,
      data,
      labelId,
      descriptionId,
      closeOnOutsideClick,
      isMounted,
      styles,
      status,
    ],
  );
}

type ContextType =
  | (ReturnType<typeof useDialog> & {
      setLabelId: React.Dispatch<React.SetStateAction<string | undefined>>;
      setDescriptionId: React.Dispatch<
        React.SetStateAction<string | undefined>
      >;
    })
  | null;

const DialogContext = React.createContext<ContextType>(null);

export const useDialogContext = () => {
  const context = React.useContext(DialogContext);

  if (context == null) {
    throw new Error("Dialog components must be wrapped in <Dialog />");
  }

  return context;
};

function Dialog({
  children,
  ...options
}: {
  children: React.ReactNode;
} & DialogOptions) {
  const dialog = useDialog(options);
  return (
    <DialogContext.Provider value={dialog}>{children}</DialogContext.Provider>
  );
}

interface DialogTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
}

export const DialogTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLProps<HTMLElement> & DialogTriggerProps
>(function DialogTrigger(
  { children, asChild = false, className, ...props },
  propRef,
) {
  "use no memo";
  const context = useDialogContext();
  const childrenRef = (children as any).ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...(children.props as any),
        "data-state": context.open ? "open" : "closed",
        className,
      }),
    );
  }

  return (
    <button
      ref={ref}
      // The user can style the trigger based on the state
      data-state={context.open ? "open" : "closed"}
      className={cn(
        "tw-border-none tw-bg-[transparent] tw-outline-none",
        className,
      )}
      {...context.getReferenceProps(props)}
    >
      {children}
    </button>
  );
});

interface DialogContentProps extends React.HTMLProps<HTMLDivElement> {
  zIndex?: number;
  overlayClassName?: string;
}

const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(
  function DialogContent(
    { className, zIndex, overlayClassName, ...props },
    propRef,
  ) {
    const {
      context: floatingContext,
      closeOnOutsideClick,
      ...context
    } = useDialogContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);

    return (
      <FloatingPortal>
        {context.isMounted && (
          <FloatingOverlay
            id={props.id}
            className={cn(
              "sd-floating-dialog-overlay tw-fixed tw-inset-0 tw-z-[10000] tw-grid tw-place-items-center tw-bg-black/70 tw-backdrop-blur-sm tw-transition-all tw-duration-200",
              overlayClassName,
              overlayClassName?.includes("tw-items-start") &&
                "!tw-items-start tw-justify-items-center",
            )}
            lockScroll
            style={{ ...context.styles, zIndex }}
            onClick={(event) => {
              if (closeOnOutsideClick && event.target === event.currentTarget) {
                context.setOpen(false);
                if (typeof closeOnOutsideClick === "function") {
                  closeOnOutsideClick();
                }
              }
            }}
          >
            <FloatingFocusManager context={floatingContext} initialFocus={-1}>
              <div
                ref={ref}
                aria-labelledby={context.labelId}
                aria-describedby={context.descriptionId}
                {...context.getFloatingProps(props)}
                className={cn(
                  "tw-scale-95 tw-opacity-0 tw-outline-none tw-transition-all tw-duration-200",
                  {
                    "tw-scale-100 tw-opacity-100": context.status === "open",
                    "tw-scale-95 tw-opacity-0": context.status === "close",
                  },
                  className,
                )}
              >
                {props.children}
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        )}
      </FloatingPortal>
    );
  },
);

export const DialogClose = React.forwardRef<
  HTMLElement,
  React.HTMLProps<HTMLElement> & DialogTriggerProps
>(function DialogTrigger(
  { children, asChild = false, className, ...props },
  propRef,
) {
  "use no memo";
  const context = useDialogContext();
  const childrenRef = (children as any).ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...(children.props as any),
        className,
        onClick: () => context.setOpen(false),
      }),
    );
  }

  return (
    <button
      ref={ref}
      onClick={() => context.setOpen(false)}
      className={cn(
        "tw-border-none tw-bg-[transparent] tw-outline-none",
        className,
      )}
      {...context.getReferenceProps(props)}
    >
      {children}
    </button>
  );
});

Dialog.Trigger = DialogTrigger;
Dialog.Content = DialogContent;
Dialog.Close = DialogClose;

export default Dialog;
